import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "sidebarEl",
    "scrimBtn",
    "openHistoryBtn",
    "searchTxt",
    "historyItemEl",
    "historyRowEl",
    "historyGroupEl",
    "emptyHistoryEl",
    "menuEl",
    "contentEl",
    "stageEl",
    "messagesEl",
    "conversationEndEl",
    "pageEndEl",
    "composerEl",
    "inputTxt",
    "sendBtn",
    "submitErrorEl",
    "responseStatusEl",
    "latestBtn",
  ];

  // Prepares responsive navigation and restores drafts without opening a mobile keyboard.
  // Called by Stimulus when the chat workspace connects.
  connect() {
    this.draftStorageKey = "fieldforms.ai.chat.drafts";
    this.scrollStorageKey = "fieldforms.ai.chat.scroll";
    this.isSubmitting = false;
    this.isFollowingConversation = true;
    this.responseRefreshTimer ??= null;
    this.mobileQuery = window.matchMedia("(width < 900px)");
    // Match the SCSS breakpoint that switches between stage and document scrolling.
    this.shortViewportQuery = window.matchMedia("(max-height: 42rem)");
    this.syncSidebar = this.syncSidebar.bind(this);
    this.resizeViewport = this.resizeViewport.bind(this);
    this.observeConversationEnd = this.observeConversationEnd.bind(this);
    this.mobileQuery.addEventListener("change", this.syncSidebar);
    this.shortViewportQuery.addEventListener("change", this.observeConversationEnd);
    window.addEventListener("resize", this.resizeViewport);
    window.visualViewport?.addEventListener("resize", this.resizeViewport);
    window.visualViewport?.addEventListener("scroll", this.resizeViewport);
    this.syncSidebar();
    this.updateViewport();
    this.conversationLoaded();
  }

  // Releases observers and page-level state when leaving the workspace.
  // Called by Stimulus when the chat shell disconnects.
  disconnect() {
    cancelAnimationFrame(this.scrollRestoreFrame);
    cancelAnimationFrame(this.viewportFrame);
    clearTimeout(this.responseRefreshTimer);
    this.viewportFrame = null;
    this.messageObserver?.disconnect();
    this.conversationObserver?.disconnect();
    this.observedConversationEnd = null;
    this.mobileQuery?.removeEventListener("change", this.syncSidebar);
    this.shortViewportQuery?.removeEventListener("change", this.observeConversationEnd);
    window.removeEventListener("resize", this.resizeViewport);
    window.visualViewport?.removeEventListener("resize", this.resizeViewport);
    window.visualViewport?.removeEventListener("scroll", this.resizeViewport);
    document.body.classList.remove("ai-chat-navigation-open");
  }

  // Resets transient drawer state before Turbo caches the current page.
  // Called by turbo:before-cache@document->ai-chat#prepareForCache on the shell.
  prepareForCache() {
    clearTimeout(this.responseRefreshTimer);
    this.saveScrollPosition();
    this.closeMenus();
    if (this.mobileQuery.matches) this.closeSidebar();
    this.isSubmitting = false;
  }

  // Opens or collapses history while retaining the user's desktop preference.
  // Called by the header button on click->ai-chat#toggleSidebar.
  toggleSidebar() {
    if (this.sidebarElTarget.inert) {
      this.element.classList.remove("is-history-collapsed");
      this.sidebarElTarget.classList.add("is-open");
      this.sidebarElTarget.inert = false;
      this.openHistoryBtnTarget.setAttribute("aria-expanded", "true");
      if (this.mobileQuery.matches) {
        this.scrimBtnTarget.classList.add("is-visible");
        this.contentElTarget.inert = true;
        document.body.classList.add("ai-chat-navigation-open");
        this.sidebarElTarget.querySelector("button").focus();
      }
    } else {
      this.closeSidebar();
    }
  }

  // Closes the history drawer and restores focus to its trigger.
  // Called by the sidebar button and scrim on click->ai-chat#closeSidebar.
  closeSidebar() {
    this.closeMenus();
    this.element.classList.add("is-history-collapsed");
    this.sidebarElTarget.classList.remove("is-open");
    this.sidebarElTarget.inert = true;
    this.contentElTarget.inert = false;
    this.scrimBtnTarget.classList.remove("is-visible");
    this.openHistoryBtnTarget.setAttribute("aria-expanded", "false");
    document.body.classList.remove("ai-chat-navigation-open");
    if (this.sidebarElTarget.contains(document.activeElement)) this.openHistoryBtnTarget.focus();
  }

  // Saves the current draft and closes mobile history before selecting a chat.
  // Called by a.chat-history-item on click->ai-chat#selectChat.
  selectChat() {
    this.closeMenus();
    this.saveDraft();
    if (this.mobileQuery.matches) this.closeSidebar();
  }

  // Filters history titles while hiding groups that have no matching chats.
  // Called by the search input on input->ai-chat#filterHistory.
  filterHistory() {
    const query = this.searchTxtTarget.value.trim().toLocaleLowerCase();
    this.closeMenus();
    this.historyRowElTargets.forEach((row) => {
      row.hidden = !row.querySelector("a.chat-history-item").textContent.toLocaleLowerCase().includes(query);
    });
    this.historyGroupElTargets.forEach((group) => {
      group.hidden = !group.querySelector(".ai-chat-history-row:not([hidden])");
    });
    this.emptyHistoryElTarget.hidden = this.historyRowElTargets.some((row) => !row.hidden);
  }

  // Loads an editable starter into the composer and places the cursor at its end.
  // Called by welcome cards on click->ai-chat#selectStarterPrompt.
  selectStarterPrompt(event) {
    this.inputTxtTarget.value = event.params.prompt;
    this.updateComposerState();
    this.saveDraft();
    this.inputTxtTarget.focus();
    this.inputTxtTarget.setSelectionRange(this.inputTxtTarget.value.length, this.inputTxtTarget.value.length);
  }

  // Stores draft changes and adjusts the send state and textarea height.
  // Called by the textarea on input->ai-chat#updateComposerState.
  updateComposerState(event) {
    if (!this.hasInputTxtTarget || !this.hasSendBtnTarget) return;
    this.inputTxtTarget.readOnly = Boolean(this.isSubmitting);
    const isPending = this.hasResponseStatusElTarget && ["queued", "generating"].includes(this.responseStatusElTarget.dataset.state);
    this.sendBtnTarget.disabled = this.isSubmitting || isPending || this.inputTxtTarget.value.trim().length === 0;
    this.sendBtnTarget.setAttribute("aria-label", isPending ? "Waiting for response" : "Send message");
    // Restore the original layout switch: longer prompts get a full-width input row.
    const hasLongPrompt = this.inputTxtTarget.value.length > 60;
    this.inputTxtTarget.form.classList.toggle("has-long-prompt", hasLongPrompt);
    this.inputTxtTarget.style.height = "auto";
    this.inputTxtTarget.style.height = `${Math.min(this.inputTxtTarget.scrollHeight, 192)}px`;
    if (event?.type === "input") this.saveDraft();
  }

  // Uses Enter to send on desktop while keeping the mobile return key multiline.
  // Called by textarea keydown->ai-chat#submitOnEnter; IME composition remains untouched.
  submitOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing || window.matchMedia("(pointer: coarse)").matches) return;
    event.preventDefault();
    if (!this.sendBtnTarget.disabled) this.inputTxtTarget.form.requestSubmit();
  }

  // Locks the composer while the message request is in progress.
  // Called by shell turbo:submit-start->ai-chat#submissionStarted for composer forms only.
  // Preserves the submitted draft so submissionFinished can restore it on failure.
  submissionStarted(event) {
    // Ignore Turbo submissions from any other form.
    if (!event.target.matches("form.new-message")) return;

    // Mark the request as pending;
    // updateComposerState uses this flag to make the textarea read-only
    // and disable the send button
    this.isSubmitting = true;

    // Capture both the conversation key and exact submitted text before Turbo replaces the composer.
    // submissionFinished uses this snapshot to remove the correct saved draft on success or restore
    // the user's message if the network request or server response fails.
    this.submittedDraft = { key: this.composerElTarget.dataset.chatKey, text: this.inputTxtTarget.value };

    // Remove error from an earlier failed attempt
    this.submitErrorElTarget.hidden = true;

    // Apply the pending state so user cannot submit the same message twice.
    this.updateComposerState();
  }

  // Clears only accepted drafts and restores input after network/server errors.
  // Called by shell turbo:submit-end->ai-chat#submissionFinished for composer forms only.
  submissionFinished(event) {
    if (!event.target.matches("form.new-message")) return;
    this.isSubmitting = false;
    if (event.detail.success) {
      if (this.submittedDraft) this.clearDraft(this.submittedDraft.key);
      if (this.hasInputTxtTarget && this.inputTxtTarget.value === this.submittedDraft?.text) this.inputTxtTarget.value = "";
    } else if (this.hasInputTxtTarget) {
      this.inputTxtTarget.value = this.submittedDraft?.text || this.inputTxtTarget.value;
      this.submitErrorElTarget.hidden = false;
    }
    this.updateComposerState();
  }

  // Synchronizes the title, selected history row, and scroll after frame navigation.
  // Called by turbo:frame-load->ai-chat#conversationLoaded on chat_content and connect().
  conversationLoaded() {
    if (!this.hasContentElTarget) return;
    this.historyItemElTargets.forEach((item) => {
      const isActive = item.pathname === window.location.pathname;
      item.classList.toggle("is-active", isActive);
      if (isActive) item.setAttribute("aria-current", "page");
      else item.removeAttribute("aria-current");
    });
    const title = this.contentElTarget.querySelector("[data-chat-title]")?.dataset.chatTitle;
    if (title) document.title = `${title === "FieldForms AI" ? title : `${title} · FieldForms AI`} · FieldForms`;
    this.restoreScrollPosition();
    this.openHistoryBtnTarget.setAttribute("aria-expanded", String(!this.sidebarElTarget.inert));
    this.scheduleResponseRefresh();
  }

  // Returns to the latest message in the active conversation scroller.
  // Called by latestBtn on click->ai-chat#scrollToConversationEnd, navigation, and streamed updates.
  scrollToConversationEnd(event) {
    if (!this.hasStageElTarget) return;
    const isPageScroll = getComputedStyle(this.stageElTarget).overflowY === "visible";
    // Finish immediately so a streaming scroll animation cannot override reading history.
    if (isPageScroll) window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "instant" });
    else this.stageElTarget.scrollTop = this.stageElTarget.scrollHeight;
    // Remember the resulting offset so scrollend can ignore this automatic movement.
    this.programmaticScroll = {
      target: isPageScroll ? document : this.stageElTarget,
      offset: isPageScroll ? window.scrollY : this.stageElTarget.scrollTop,
    };
    this.isFollowingConversation = true;
    if (this.hasLatestBtnTarget) this.latestBtnTarget.hidden = true;

    // An explicit jump changes the saved preference from reading history to following latest.
    if (event?.type === "click") this.saveScrollPosition();
  }

  // Saves a reading position once the active conversation scroller stops moving.
  // Called by stageEl on scrollend->ai-chat#scrollEnded and the shell's scrollend@document action.
  scrollEnded(event) {
    if (this.isRestoringScroll || !this.hasStageElTarget) return;
    const target = this.conversationScrollRoot || document;
    // Ignore history, nested message scrollers, and the inactive document or stage.
    if (event.target !== target || event.currentTarget !== target) return;

    const offset = target === document ? window.scrollY : target.scrollTop;
    // Streaming and restoration also fire scrollend; only save if the reader moved elsewhere.
    if (this.programmaticScroll?.target === target && this.programmaticScroll.offset === offset) return;
    this.programmaticScroll = null;
    this.saveScrollPosition();
  }

  // Copies the visible answer and reports clipboard failures inline.
  // Called by assistant footer buttons on click->ai-chat#copyResponse.
  async copyResponse(event) {
    const button = event.currentTarget;
    const label = button.querySelector("span");
    try {
      await navigator.clipboard.writeText(button.closest("article").querySelector(".ai-chat-message-body").innerText);
      label.textContent = "Copied";
    } catch (error) {
      console.error("[ai-chat] Failed to copy response", error);
      label.textContent = "Select text to copy";
    }
  }

  // Keeps keyboard focus inside mobile history and dismisses open panels on Escape.
  // Called by the shell on keydown->ai-chat#handleKeydown.
  handleKeydown(event) {
    const openMenu = this.element.querySelector(".ai-chat-details-panel:popover-open");
    if (event.key === "Escape" && openMenu) {
      event.preventDefault();
      openMenu.hidePopover();
      document.getElementById(openMenu.dataset.triggerId)?.focus();
      return;
    }
    if (event.key === "Escape" && this.mobileQuery.matches && !this.sidebarElTarget.inert) this.closeSidebar();
    if (openMenu) return;
    if (event.key !== "Tab" || !this.mobileQuery.matches || this.sidebarElTarget.inert) return;
    const controls = [...this.sidebarElTarget.querySelectorAll("a, button, input")].filter((control) => control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Reveals server validation feedback in the selected conversation's options.
  // Called by Stimulus when a menuEl target connects after an unsuccessful rename.
  menuElTargetConnected(element) {
    if (element.dataset.openOnConnect === "true") element.showPopover();
  }

  // Fits a native popover beside its trigger without clipping the sidebar or viewport.
  // Called by div.ai-chat-details-panel on toggle->ai-chat#positionMenu.
  positionMenu(event) {
    if (event.newState !== "open") return;
    this.placeMenus([event.currentTarget]);
  }

  // Repositions the menu when its inline rename form expands or collapses.
  // Called by details.ai-chat-rename on toggle->ai-chat#resizeMenu.
  resizeMenu(event) {
    const menu = event.currentTarget.closest(".ai-chat-details-panel");
    if (menu.matches(":popover-open")) this.placeMenus([menu]);
  }

  // Closes top-layer menus before history changes or the workspace is cached.
  // Called by history selection/search, drawer closing, cache preparation, and nav.ai-chat-history scroll->ai-chat#closeMenus.
  closeMenus() {
    this.element.querySelectorAll(".ai-chat-details-panel:popover-open").forEach((menu) => menu.hidePopover());
  }

  // Fits open panels within the visible viewport, including above the mobile keyboard.
  // Called by positionMenu(), resizeMenu(), and updateViewport().
  placeMenus(menus) {
    if (menus.length === 0) return;
    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft || 0;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportWidth = viewport?.width || window.innerWidth;
    const viewportHeight = viewport?.height || window.innerHeight;
    const maxHeight = `${Math.max(120, viewportHeight - 24)}px`;

    // Apply height limits first; wrapping and scrollbars can change the measured size.
    for (const menu of menus) {
      if (menu.style.maxHeight !== maxHeight) menu.style.maxHeight = maxHeight;
    }

    // Measure all panels before writing positions to avoid alternating reads and writes.
    const positions = menus.map((menu) => {
      const trigger = document.getElementById(menu.dataset.triggerId);
      if (!trigger) return null;
      const rect = trigger.getBoundingClientRect();
      const bounds = menu.getBoundingClientRect();
      return {
        menu,
        left: `${Math.max(viewportLeft + 12, Math.min(rect.right - bounds.width, viewportLeft + viewportWidth - bounds.width - 12))}px`,
        top: `${Math.max(viewportTop + 12, Math.min(rect.bottom + 8, viewportTop + viewportHeight - bounds.height - 12))}px`,
      };
    });

    // Leave unchanged styles alone when multiple viewport events report the same layout.
    for (const position of positions) {
      if (!position) continue;
      const { menu, left, top } = position;
      if (menu.style.left !== left) menu.style.left = left;
      if (menu.style.top !== top) menu.style.top = top;
    }
  }

  // Restores a per-conversation draft when Turbo replaces the textarea.
  // Called by Stimulus whenever the inputTxt target is connected.
  inputTxtTargetConnected() {
    // Wait for the surrounding composer target so new-chat restoration uses the correct key.
    queueMicrotask(() => this.restoreDraft());
    this.updateComposerState();
  }

  // Restores the draft for the currently connected conversation composer.
  // Called after inputTxt connects, once Stimulus has connected the composer target.
  restoreDraft() {
    if (!this.hasInputTxtTarget) return;
    const key = this.hasComposerElTarget ? this.composerElTarget.dataset.chatKey : "new";
    const draft = this.loadDraft(key);
    if (draft && !this.inputTxtTarget.value) {
      this.inputTxtTarget.value = draft;
      this.updateComposerState();
    }
  }

  // Updates composer availability when a server-rendered status arrives.
  // Called by Stimulus when responseStatusEl is connected or replaced by Turbo.
  responseStatusElTargetConnected() {
    this.updateComposerState();
    this.scheduleResponseRefresh();
  }

  // Schedules a frame refresh while a response is queued or generating.
  // Called when the conversation loads or Turbo replaces its response status.
  scheduleResponseRefresh() {
    clearTimeout(this.responseRefreshTimer);
    if (!this.hasResponseStatusElTarget || !this.hasContentElTarget) return;

    const isPending = ["queued", "generating"].includes(this.responseStatusElTarget.dataset.state);
    if (isPending) this.responseRefreshTimer = setTimeout(() => this.refreshPendingResponse(), 2000);
  }

  // Recovers a completed reply if its broadcast arrived before the page subscribed.
  // Called by the pending-response timer until server-rendered status changes.
  refreshPendingResponse() {
    if (!this.hasContentElTarget || !this.hasResponseStatusElTarget) return;
    const isPending = ["queued", "generating"].includes(this.responseStatusElTarget.dataset.state);
    if (!isPending) return;

    const frame = this.contentElTarget;
    if (frame.complete) {
      // A frame without src needs its chat URL before Turbo can reload it.
      if (frame.src !== window.location.pathname) frame.src = window.location.pathname;
      else frame.reload();
    }
    this.scheduleResponseRefresh();
  }

  // Observes streamed text without pulling readers away from earlier messages.
  // Called by Stimulus when a conversation's messagesEl target connects.
  messagesElTargetConnected(element) {
    this.isRestoringScroll = true;
    this.messageObserver?.disconnect();
    this.messageObserver = new MutationObserver(() => {
      if (this.isRestoringScroll) return;
      if (this.isFollowingConversation) this.scrollToConversationEnd();
      else if (this.hasLatestBtnTarget) this.latestBtnTarget.hidden = false;
    });
    this.messageObserver.observe(element, { childList: true, subtree: true, characterData: true });
  }

  // Releases the observer when its conversation leaves the frame.
  // Called by Stimulus when the messagesEl target disconnects.
  messagesElTargetDisconnected() {
    this.messageObserver?.disconnect();
  }

  // Reconnects bottom tracking after Turbo replaces the conversation markup.
  // Called by Stimulus when the pageEndEl target connects.
  pageEndElTargetConnected() {
    this.observeConversationEnd();
  }

  // Releases bottom tracking before a conversation is replaced or removed.
  // Called by Stimulus when the pageEndEl target disconnects.
  pageEndElTargetDisconnected() {
    cancelAnimationFrame(this.scrollRestoreFrame);
    this.conversationObserver?.disconnect();
    this.observedConversationEnd = null;
  }

  // Observes the last 100px of the active scroller without per-scroll layout reads.
  // Called by target connection, scroll restoration, and the short-viewport breakpoint change.
  observeConversationEnd() {
    if (this.isRestoringScroll) return;
    if (!this.hasStageElTarget || !this.hasConversationEndElTarget || !this.hasPageEndElTarget) return;
    const isPageScroll = getComputedStyle(this.stageElTarget).overflowY === "visible";
    // A null root observes the document viewport; otherwise the stage owns scrolling.
    const root = isPageScroll ? null : this.stageElTarget;
    const sentinel = isPageScroll ? this.pageEndElTarget : this.conversationEndElTarget;
    // Reuse the observer unless Turbo or the responsive layout changed its target.
    if (this.observedConversationEnd === sentinel && this.conversationScrollRoot === root) return;

    // Short viewports include the composer in document scrolling, so observe its bottom.
    this.conversationObserver?.disconnect();
    this.observedConversationEnd = sentinel;
    this.conversationScrollRoot = root;
    this.conversationObserver = new IntersectionObserver(
      (entries) => {
        if (this.isRestoringScroll) return;
        const entry = entries.at(-1);
        this.isFollowingConversation = entry.isIntersecting;
        if (this.hasLatestBtnTarget) this.latestBtnTarget.hidden = this.isFollowingConversation || !this.hasMessagesElTarget;
      },
      { root, rootMargin: "0px 0px 100px 0px", threshold: 0 },
    );
    this.conversationObserver.observe(sentinel);
  }

  // Saves the outgoing frame before Turbo removes its measurable content.
  // Called by chat_content on turbo:before-frame-render->ai-chat#conversationLeaving.
  conversationLeaving(event) {
    if (event.target !== this.contentElTarget) return;
    // Capture the old DOM before blocking streamed updates during frame replacement.
    this.saveScrollPosition();
    this.isRestoringScroll = true;
    cancelAnimationFrame(this.scrollRestoreFrame);
  }

  // Returns the current scroller and its visible top for message anchoring.
  // Called only when saving or restoring a conversation position.
  conversationScrollGeometry() {
    const stage = this.stageElTarget;
    const isPageScroll = getComputedStyle(stage).overflowY === "visible";
    const scroller = isPageScroll ? document.documentElement : stage;
    return {
      scroller,
      isPageScroll,
      top: isPageScroll ? 0 : stage.getBoundingClientRect().top + stage.clientTop,
      offset: isPageScroll ? window.scrollY : stage.scrollTop,
      height: isPageScroll ? window.innerHeight : stage.clientHeight,
    };
  }

  // Stores the current chat's reading position in the shared localStorage map.
  // Called by scrollEnded(), explicit jumps, navigation, and pagehide@window->ai-chat#saveScrollPosition.
  saveScrollPosition() {
    if (this.isRestoringScroll || !this.hasMessagesElTarget || !this.hasComposerElTarget || !this.hasStageElTarget) return;
    const key = this.composerElTarget.dataset.chatKey;
    if (!key || key === "new") return;
    const geometry = this.conversationScrollGeometry();
    // Read the final position because the bottom observer may not have delivered its latest state.
    const isFollowingConversation = geometry.scroller.scrollHeight - geometry.offset - geometry.height <= 100;
    let anchor = null;
    let anchorOffset = 0;
    if (!isFollowingConversation) {
      // Anchor to the first message reaching below the visible top, including a partially read message.
      for (const message of this.messagesElTarget.querySelectorAll(":scope > [id]")) {
        const rect = message.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom > geometry.top) {
          anchor = message.id;
          anchorOffset = rect.top - geometry.top;
          break;
        }
      }
    }
    // Merge this chat into the latest stored map so other chats keep their positions.
    const positions = this.readScrollPositions();
    positions[key] = { isFollowingConversation, anchor, anchorOffset, offset: geometry.offset };
    try {
      localStorage.setItem(this.scrollStorageKey, JSON.stringify(positions));
      // console.log("[ai-chat] Scroll position saved", { chatId: key, ...positions[key] });
    } catch (error) {
      console.error("[ai-chat] Failed to save scroll position", error);
      // Keep navigation available when local storage is blocked or full.
    }
  }

  // Restores a message anchor after the composer and Turbo frame have settled.
  // Called by conversationLoaded() on initial connection and frame navigation.
  restoreScrollPosition() {
    // Cancel an older restore and pause following while the new frame settles.
    this.programmaticScroll = null;
    cancelAnimationFrame(this.scrollRestoreFrame);
    this.isRestoringScroll = true;
    this.conversationObserver?.disconnect();
    this.observedConversationEnd = null;
    // Wait for Turbo targets and the restored composer draft to establish the layout.
    this.scrollRestoreFrame = requestAnimationFrame(() => {
      const key = this.hasComposerElTarget ? this.composerElTarget.dataset.chatKey : "new";
      const snapshot = this.readScrollPositions()[key];
      // Only history positions need an anchor; following-latest snapshots reopen at the bottom.
      const hasSavedPosition = snapshot?.isFollowingConversation === false && Number.isFinite(snapshot.offset) && Number.isFinite(snapshot.anchorOffset);

      if (this.hasMessagesElTarget && hasSavedPosition) {
        const geometry = this.conversationScrollGeometry();
        const anchor = typeof snapshot.anchor === "string" ? document.getElementById(snapshot.anchor) : null;
        const hasAnchor = anchor && this.messagesElTarget.contains(anchor);
        // Move by the difference between the anchor's current and saved viewport offsets.
        const offset = hasAnchor ? geometry.offset + anchor.getBoundingClientRect().top - geometry.top - snapshot.anchorOffset : snapshot.offset;
        // Apply one immediate adjustment; deleted anchors fall back to their old scroll offset.
        if (geometry.isPageScroll) window.scrollTo({ top: offset, behavior: "instant" });
        else geometry.scroller.scrollTo({ top: offset, behavior: "instant" });
        // Suppress the later scrollend event even though restoration finishes synchronously here.
        this.programmaticScroll = {
          target: geometry.isPageScroll ? document : geometry.scroller,
          offset: geometry.isPageScroll ? window.scrollY : geometry.scroller.scrollTop,
        };
        this.isFollowingConversation = false;
        if (this.hasLatestBtnTarget) this.latestBtnTarget.hidden = false;
      } else if (this.hasMessagesElTarget) {
        this.scrollToConversationEnd();
      } else {
        this.isFollowingConversation = true;
        if (this.hasStageElTarget) this.stageElTarget.scrollTop = 0;
        if (this.hasLatestBtnTarget) this.latestBtnTarget.hidden = true;
      }

      // Start fresh observation so restoration cannot leave a stale following state.
      this.isRestoringScroll = false;
      this.observeConversationEnd();
    });
  }

  // Reads scroll positions keyed by chat ID, ignoring malformed or unavailable storage.
  // Called by saveScrollPosition() and restoreScrollPosition().
  readScrollPositions() {
    try {
      const positions = JSON.parse(localStorage.getItem(this.scrollStorageKey) || "{}");
      return positions && typeof positions === "object" && !Array.isArray(positions) ? positions : {};
    } catch (error) {
      console.error("[ai-chat] Failed to read scroll positions", error);
      return {};
    }
  }

  // Reads all saved drafts from localStorage and ignores malformed or unavailable storage.
  // Called by loadDraft() and saveDraft() for the current chat workspace.
  readDrafts() {
    try {
      return JSON.parse(localStorage.getItem(this.draftStorageKey) || "{}");
    } catch (error) {
      console.error("[ai-chat] Failed to read drafts", error);
      return {};
    }
  }

  // Loads the draft saved for one conversation ID.
  // Called when Stimulus connects the composer textarea for a conversation.
  loadDraft(key) {
    return this.readDrafts()[key] || "";
  }

  // Saves the current draft under its conversation ID for restoration after navigation or reload.
  // Called by input changes, submission, and history navigation.
  saveDraft() {
    if (!this.hasComposerElTarget || !this.hasInputTxtTarget) return;
    const drafts = this.readDrafts();
    drafts[this.composerElTarget.dataset.chatKey] = this.inputTxtTarget.value;
    try {
      localStorage.setItem(this.draftStorageKey, JSON.stringify(drafts));
    } catch (error) {
      console.error("[ai-chat] Failed to save draft", error);
      // Continue without persistence when browser storage is unavailable.
    }
  }

  // Removes the submitted conversation's draft after the server accepts the message.
  // Called by submissionFinished so drafts from other conversations remain intact.
  clearDraft(key) {
    const drafts = this.readDrafts();
    delete drafts[key];
    try {
      localStorage.setItem(this.draftStorageKey, JSON.stringify(drafts));
    } catch (error) {
      console.error("[ai-chat] Failed to clear draft", error);
      // Continue without cleanup when browser storage is unavailable.
    }
  }

  // Resets sidebar access when crossing the mobile breakpoint.
  // Called by connect() and the responsive media query change listener.
  syncSidebar() {
    if (this.mobileQuery.matches) this.closeSidebar();
    else {
      this.element.classList.remove("is-history-collapsed");
      this.sidebarElTarget.inert = false;
      this.contentElTarget.inert = false;
      this.openHistoryBtnTarget.setAttribute("aria-expanded", "true");
      this.scrimBtnTarget.classList.remove("is-visible");
      document.body.classList.remove("ai-chat-navigation-open");
    }
  }

  // Combines overlapping viewport events into one update per animation frame.
  // Called by window resize and visualViewport resize/scroll listeners.
  resizeViewport() {
    // Keep keyboard movement responsive without scheduling duplicate work in the same frame.
    if (this.viewportFrame != null) return;
    this.viewportFrame = requestAnimationFrame(() => {
      this.viewportFrame = null;
      this.updateViewport();
    });
  }

  // Sizes the workspace to the visible area above a mobile software keyboard.
  // Called by connect() immediately and resizeViewport() on the next animation frame.
  updateViewport() {
    // Read the visible viewport and desktop header offset before changing workspace styles.
    const viewport = window.visualViewport;
    const viewportHeight = viewport?.height || window.innerHeight;
    const viewportTop = viewport?.offsetTop || 0;
    const top = this.mobileQuery.matches ? viewportTop : Math.max(viewportTop, this.element.getBoundingClientRect().top);
    const topValue = `${viewportTop}px`;
    const heightValue = `${Math.max(0, viewportHeight + viewportTop - top)}px`;

    // Follow Safari's keyboard pan, but only write values that actually changed.
    const style = this.element.style;
    if (style.getPropertyValue("--chat-viewport-top") !== topValue) style.setProperty("--chat-viewport-top", topValue);
    if (style.getPropertyValue("--chat-viewport-height") !== heightValue) style.setProperty("--chat-viewport-height", heightValue);

    // Menu triggers must be measured after the workspace adopts its new size.
    const menus = [...this.element.querySelectorAll(".ai-chat-details-panel:popover-open")];
    this.placeMenus(menus);
  }
}
